import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Plus, Download, Upload, FileText, Wrench, Flame, Cable, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import type { Materials } from '../../../types/materials';
import { toast } from 'react-hot-toast';
import { AddMaterialDialog } from './AddMaterialDialog';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import { cn } from '../../../../../lib/utils';
import { materialsList, pipeSizes } from '../../../mocks/material';
import { downloadMaterialTemplate, processMaterialUpload } from '../../../../../lib/materialUtils';
import { PipeSize } from '@/features/project/types';
import { PipeTab } from './tabs/PipeTab';
import { ForgeTab } from './tabs/ForgeTab';
import { GasTab } from './tabs/GasTab';
import { WireTab } from './tabs/WireTab';
import { CoatingTab } from './tabs/CoatingTab';

interface ProjectMaterialsProps {
  project: DashboardProject;
}

const MATERIAL_CATEGORIES = {
  'Pipe': 'Pipes',
  'Forge': 'Forges',
  'Gas': 'Gas Cylinders',
  'Wire Lot': 'Wire Lots',
  'Coating': 'Coating'
} as const;

export const ProjectMaterials: React.FC<ProjectMaterialsProps> = ({ project }) => {
  const [materials, setMaterials] = useState<Materials[]>(materialsList);
  const [isAddingMaterial, setIsAddingMaterial] = useState(false);
  const [selectedPipeSize, setSelectedPipeSize] = useState<number>(0);
  const [selectedSize, setSelectedSize] = useState<string>('0');
  const [materialTab, setMaterialTab] = useState<'pipe' | 'forge' | 'gas' | 'wire' | 'coating'>('pipe');
  const [materialsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [pipeSize, setPipeSize] = useState<PipeSize[]>(pipeSizes);

  useEffect(() => {
    const firstAvailableIndex = pipeSize.findIndex((_, index) => {
      if (index === 0 && project.projectTypes.pipeline) return true;
      if (index === 1 && project.projectTypes.fabrication) return true;
      return false;
    });

    if (firstAvailableIndex !== -1) {
      setSelectedPipeSize(firstAvailableIndex);
      setSelectedSize(firstAvailableIndex.toString());
      if (firstAvailableIndex === 0) setMaterialTab('pipe');
      if (firstAvailableIndex === 1) setMaterialTab('forge');
    }
  }, [project.projectTypes, pipeSize]);

  const handleAddMaterial = (material: Materials) => {
    setMaterials(prev => [...prev, material]);
  };

  const handleImport = () => {
    // TODO: Implement import
  };

  const handleExport = () => {
    // TODO: Implement export
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const TABS_CONFIG = {
    pipe: {
      label: 'Base Pipes',
      icon: FileText,
      content: () => (
        <PipeTab
          materials={materials}
          selectedPipeSize={selectedPipeSize}
          currentPage={currentPage}
          materialsPerPage={materialsPerPage}
          onPageChange={handlePageChange}
        />
      )
    },
    forge: {
      label: 'Forges',
      icon: Wrench,
      content: () => (
        <ForgeTab
          materials={materials}
          selectedPipeSize={selectedPipeSize}
          currentPage={currentPage}
          materialsPerPage={materialsPerPage}
          onPageChange={handlePageChange}
        />
      )
    },
    gas: {
      label: 'Gas',
      icon: Flame,
      content: () => (
        <GasTab
          materials={materials}
          selectedPipeSize={selectedPipeSize}
          currentPage={currentPage}
          materialsPerPage={materialsPerPage}
          onPageChange={handlePageChange}
        />
      )
    },
    wire: {
      label: 'Wire Lot',
      icon: Cable,
      content: () => (
        <WireTab
          materials={materials}
          selectedPipeSize={selectedPipeSize}
          currentPage={currentPage}
          materialsPerPage={materialsPerPage}
          onPageChange={handlePageChange}
        />
      )
    },
    coating: {
      label: 'Coating',
      icon: Cable,
      content: () => (
        <CoatingTab
          materials={materials}
          selectedPipeSize={selectedPipeSize}
          currentPage={currentPage}
          materialsPerPage={materialsPerPage}
          onPageChange={handlePageChange}
        />
      )
    }
  } as const;

  return (
    <Card className="border-none shadow-none">
      <CardContent className="p-6">
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold">Material Traceability</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  try {
                    downloadMaterialTemplate();
                    toast.success('Template downloaded successfully');
                  } catch (error) {
                    console.error('Error downloading template:', error);
                    toast.error('Failed to download template');
                  }
                }}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center"
              >
                <Download className="h-4 w-4" />
                Download Template
              </Button>
              <Button
                onClick={() => document.getElementById('bulk-upload')?.click()}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center"
              >
                <Upload className="h-4 w-4" />
                Upload Materials
                <input
                  id="bulk-upload"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  aria-label="Upload materials file"
                  onChange={async (e) => {
                    if (e.target.files && e.target.files[0]) {
                      const loadingToast = toast.loading('Processing materials...');
                      try {
                        const result = await processMaterialUpload(e.target.files[0]);
                        console.log('Processed upload data:', result);
                        toast.success('Materials uploaded successfully', {
                          id: loadingToast,
                        });
                      } catch (error) {
                        console.error('Error processing file:', error);
                        toast.error('Failed to process file. Please check the format and try again.', {
                          id: loadingToast,
                        });
                      }
                      e.target.value = '';
                    }
                  }}
                />
              </Button>
              <Button
                onClick={handleExport}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 min-w-[140px] justify-center"
              >
                <FileText className="h-4 w-4" />
                Export Report
              </Button>
              <Button
                onClick={() => setIsAddingMaterial(true)}
                size="sm"
                className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[140px] justify-center"
              >
                <Plus className="h-4 w-4" />
                Add Material
              </Button>
            </div>
          </div>

          {/* Pipe Size Selection */}
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <Label>Pipe Size:</Label>
              <Select
                value={selectedPipeSize.toString()}
                onValueChange={(value: string) => {
                  console.log(value)
                  if (value === '0') {
                    setMaterialTab('pipe')
                  }
                  else if (value === '1') {
                    setMaterialTab('forge')
                  }
                  else if (value === '2') {
                    setMaterialTab('gas')
                  }
                  else if (value === '3') {
                    setMaterialTab('wire')
                  }
                  setSelectedSize(value)
                  setSelectedPipeSize(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[400px]">
                  <SelectValue placeholder="Select pipe size" />
                </SelectTrigger>
                <SelectContent>
                  {pipeSize.map((pipe, index) => {
                    // Only show pipeline option if project type is pipeline
                    if (index === 0 && project.projectTypes.pipeline) {
                      return (
                        <SelectItem key={index} value={index.toString()}>
                          Pipeline ({pipe.size} × {pipe.wallThickness})
                        </SelectItem>
                      );
                    }
                    // Only show buckle arrestor option if project type is fabrication
                    if (index === 1 && project.projectTypes.fabrication) {
                      return (
                        <SelectItem key={index} value={index.toString()}>
                          Buckle Arrestor ({pipe.size} × {pipe.wallThickness})
                        </SelectItem>
                      );
                    }
                    return null;
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Material Category Tabs */}
          <Tabs className="w-full"
            value={materialTab}
            onValueChange={(value) => setMaterialTab(value as 'pipe' | 'forge' | 'gas' | 'wire' | 'coating')}
          >
             <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 rounded-t-lg">
             <TabsList className="h-12 w-full justify-start gap-8 border-b border-gray-200 bg-transparent px-4 py-8">
            
              {Object.entries(TABS_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
            
                if (key === 'forge' && selectedSize !== '1') return null;
                if (key === 'forge' && !project.projectTypes.fabrication) return null;
                if (key === 'coating' && !project.features.coating) return null;
  
                return (

                  <TabsTrigger 
                    key={key}
                    value={key}
                    
                    className="data-[state=active]:border-[#e04530] data-[state=active]:text-[#e04530] relative px-1 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px]"
                  >
                      <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {config.label}
                          </div>
                  </TabsTrigger>
                );
              })}
            </TabsList>
            </div>
            {Object.entries(TABS_CONFIG).map(([key, config]) => (
              <TabsContent key={key} value={key}>
                {config.content()}
              </TabsContent>
            ))}
          </Tabs>

          <AddMaterialDialog
            isOpen={isAddingMaterial}
            onClose={() => setIsAddingMaterial(false)}
            onAdd={handleAddMaterial}
            selectedPipeSize={selectedPipeSize}
          />
        </>
      </CardContent>
    </Card>
  );
}; 